import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { Camera, Images, LogOut, Play, UserRound, type LucideIcon } from 'lucide-react';
import 'primereact/resources/themes/lara-light-green/theme.css';
import 'primeicons/primeicons.css';
import './styles/main.scss';

import UploadPage from './pages/UploadPage';
import LibraryPage from './pages/LibraryPage';
import ProfilePage from './pages/ProfilePage';
import LoginPage from './pages/LoginPage';
import PlayPage from './pages/PlayPage';
import OnboardingPage from './pages/OnboardingPage';
import AccountLockedPage from './pages/AccountLockedPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import WelcomePage from './pages/WelcomePage';
import RedeemPage from './pages/RedeemPage';
import PromoLandingPage from './pages/PromoLandingPage';
import SetupPage from './pages/SetupPage';
import { useAuth } from './auth/useAuth';
import { useProfile } from './context/ProfileContext';
import { useDeviceCourses } from './context/DeviceCoursesContext';
import { swingApi } from './api/swingApi';
import { RequireAuth } from './auth/RequireAuth';
import { rememberMobileReturn, validateMobileReturn } from './mobileReturn';
import { clearPromoCampaign } from './promoCampaign';
import { usePromoClaim } from './hooks/usePromoClaim';
import DevWatermark from './components/DevWatermark';

const App: React.FC = () => (
  <PrimeReactProvider>
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
    <DevWatermark />
  </PrimeReactProvider>
);

const AuthLoader: React.FC<{ label?: string }> = ({ label = 'Signing in' }) => (
  <div className="auth-loading" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} aria-label={label} />
  </div>
);

/**
 * Full access gate for the main app. A golfer only reaches the protected pages once
 * they are authenticated, are not locked out, have completed profile verification,
 * and have selected a membership. Otherwise they are routed to the appropriate setup step.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isHydrated, hasMembership, isLockedOut, verification, isVerificationHydrated } = useProfile();
  const location = useLocation();
  const mobileReturn = validateMobileReturn(new URLSearchParams(location.search).get('mobileReturn'));
  const isSetUp = isHydrated && hasMembership && Boolean(verification?.isProfileConfirmed);

  // A golfer who is already set up can't take a new-golfer offer, so forget any promo link they opened.
  useEffect(() => {
    if (isSetUp) clearPromoCampaign();
  }, [isSetUp]);

  if (isLoading) return <AuthLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isHydrated || !isVerificationHydrated) return <AuthLoader label="Loading your profile" />;
  if (isLockedOut) return <Navigate to="/account-locked" replace />;
  if (!verification?.isVerified) return <Navigate to="/verify-email" replace />;
  if (!verification.isProfileConfirmed || !hasMembership) {
    const onboardingTarget = mobileReturn
      ? `/onboarding?mobileReturn=${encodeURIComponent(mobileReturn)}`
      : '/onboarding';
    return <Navigate to={onboardingTarget} replace />;
  }

  return <>{children}</>;
};

const VerifiedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { isHydrated, isLockedOut, verification, isVerificationHydrated } = useProfile();
  if (isLoading) return <AuthLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isHydrated || !isVerificationHydrated) return <AuthLoader label="Loading your profile" />;
  if (isLockedOut) return <Navigate to="/account-locked" replace />;
  if (!verification?.isVerified) return <Navigate to="/verify-email" replace />;
  return <>{children}</>;
};

const SetupRequiredRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useProfile();
  const { savedCourses, hydrated } = useDeviceCourses();
  const [clubStatus, setClubStatus] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    setClubStatus('loading');
    swingApi.getAverages(profile.userId)
      .then((averages) => {
        if (!cancelled) setClubStatus(averages.length > 0 ? 'ready' : 'missing');
      })
      .catch(() => {
        if (!cancelled) setClubStatus('error');
      });
    return () => { cancelled = true; };
  }, [profile.userId]);

  if (!hydrated || clubStatus === 'loading') return <AuthLoader label="Checking your setup" />;
  if (savedCourses.length === 0 || clubStatus === 'missing') return <Navigate to="/setup" replace />;
  return <>{children}</>;
};

const MembershipRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/profile${location.search}`} replace />;
};

const ClubhouseHandoffRoute: React.FC = () => {
  const { loginClubhouseHandoff } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const token = new URLSearchParams(window.location.hash.slice(1)).get('token');
    const mobileReturn = validateMobileReturn(new URLSearchParams(location.search).get('mobileReturn'));
    rememberMobileReturn(mobileReturn);
    window.history.replaceState(null, '', '/auth/handoff');
    if (!token) {
      window.location.replace('/login');
      return;
    }

    loginClubhouseHandoff(token)
      .then(() => window.location.replace(
        mobileReturn
          ? `/profile?mobileReturn=${encodeURIComponent(mobileReturn)}`
          : '/profile',
      ))
      .catch(() => window.location.replace('/login'));
  }, [location.search, loginClubhouseHandoff]);

  return <AuthLoader label="Opening Membership" />;
};

/**
 * Landing route. While Entra External ID (MSAL) is still resolving the session —
 * including handling the redirect back from the hosted sign-in page — we must NOT
 * navigate, or React Router would strip the auth response params before MSAL can
 * process them (which manifests as "login loops back to the page"). Once MSAL
 * settles, route to the app or the login screen.
 */
const RootRoute: React.FC = () => {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return (
      <div className="auth-loading" style={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} aria-label="Signing in" />
      </div>
    );
  }
  return <Navigate to={isAuthenticated ? '/play' : '/login'} replace />;
};

type NavItem = { to: string; Icon: LucideIcon; label: string };

const NAV_ITEMS: NavItem[] = [
  { to: '/play', Icon: Play, label: 'Play' },
  { to: '/upload', Icon: Camera, label: 'Upload' },
  { to: '/library', Icon: Images, label: 'Library' },
  { to: '/profile', Icon: UserRound, label: 'Profile' },
];

const AppShell: React.FC = () => {
  const { profile, logout } = useProfile();
  const location = useLocation();
  usePromoClaim();
  const CHROMELESS_ROUTES = [
    '/login',
    '/onboarding',
    '/setup',
    '/account-locked',
    '/verify-email',
    '/confirm-email',
    '/welcome',
    '/auth/handoff',
  ];
  const isChromeless =
    CHROMELESS_ROUTES.includes(location.pathname)
    || location.pathname.startsWith('/redeem/')
    || location.pathname.startsWith('/promo/');

  const hasAvatar = Boolean(profile.avatarDataUrl);
  const avatarSrc = profile.avatarDataUrl;

    return (
      <div className={`app-layout${isChromeless ? ' app-layout--login' : ''}`}>
        {/* Top Navigation */}
        {!isChromeless && (
        <header className="app-nav">
          <nav className="app-nav__inner">
            <NavLink to="/upload" className="app-nav__brand">
              <img src={`${process.env.PUBLIC_URL}/caddiesight-mark.svg`} alt="" className="app-nav__mark" />
              <span className="app-nav__title"><span>Caddie</span><span>Sight</span></span>
            </NavLink>
            <div className="app-nav__menu">
              <div className="app-nav__links">
                {NAV_ITEMS.map(({ to, Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) => `app-nav__link${isActive ? ' active' : ''}`}
                  >
                    {to === '/profile' && hasAvatar ? (
                      <img src={avatarSrc} alt="" className="nav-avatar" />
                    ) : (
                      <Icon className="ui-icon" strokeWidth={1.5} aria-hidden="true" />
                    )}
                    {label}
                  </NavLink>
                ))}
              </div>
              <button type="button" className="app-nav__link app-nav__sign-out" onClick={logout}>
                <LogOut className="ui-icon" strokeWidth={1.5} aria-hidden="true" />
                Sign Out
              </button>
            </div>
          </nav>
        </header>
        )}

        {/* Page Content */}
        <main className={`main-content${isChromeless ? ' main-content--login' : ''}`}>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/handoff" element={<ClubhouseHandoffRoute />} />
            <Route path="/verify-email" element={<RequireAuth><VerifyEmailPage /></RequireAuth>} />
            <Route path="/confirm-email" element={<ConfirmEmailPage />} />
            <Route path="/onboarding" element={<VerifiedRoute><OnboardingPage /></VerifiedRoute>} />
            <Route path="/account-locked" element={<RequireAuth><AccountLockedPage /></RequireAuth>} />
            <Route path="/redeem/:token" element={<RequireAuth><RedeemPage /></RequireAuth>} />
            <Route path="/promo/:slug" element={<PromoLandingPage />} />
            <Route path="/setup" element={<ProtectedRoute><SetupPage /></ProtectedRoute>} />
            <Route path="/play" element={<ProtectedRoute><SetupRequiredRoute><PlayPage /></SetupRequiredRoute></ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute><SetupRequiredRoute><UploadPage /></SetupRequiredRoute></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><LibraryPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/membership" element={<MembershipRedirect />} />
            <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Bottom Tab Navigation (mobile-first) */}
        {!isChromeless && (
        <footer className="bottom-nav">
          {NAV_ITEMS.map(({ to, Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => (isActive ? 'active' : '')}
            >
              {to === '/profile' && hasAvatar ? (
                <img src={avatarSrc} alt="" className="nav-avatar" />
              ) : (
                <Icon className="ui-icon" strokeWidth={1.5} aria-hidden="true" />
              )}
              {label}
            </NavLink>
          ))}
        </footer>
        )}
      </div>
  );
};

export default App;

